import { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../api/client';
import { StatusBadge } from '../../components/shared/StatusBadge';
import { useSSEStore } from '../../stores/sseStore';
import { usePagination } from '../../hooks/usePagination';
import { Pagination } from '../../components/shared/Pagination';
import {
  RefreshCw, Search, X, ChevronDown, ChevronUp, Send,
  Package, Truck, DollarSign, AlertTriangle, MessageCircle,
  ExternalLink, Clock, ShoppingCart, Filter, Printer, FileText,
  CheckSquare, Square, Loader2
} from 'lucide-react';
import { secureUrl } from '../../utils/url';
import { EntityLink } from '../../components/shared/EntityLink';
import { useCan } from '../../lib/rbac';

// ── Types ──
interface OrderItem {
  ml_order_id: number;
  item_id: string;
  seller_sku: string;
  quantity: number;
  unit_price: number;
  sale_fee: number;
  currency_id: string;
  title: string | null;
  thumbnail: string | null;
}

interface Shipment {
  ml_shipment_id: number;
  status: string;
  substatus: string | null;
  tracking_number: string | null;
  shipping_method_name: string | null;
  logistic_type: string | null;
  logistic_mode: string | null;
  shipping_cost: number | null;
  estimated_delivery_date: string | null;
  shipped_at: string | null;
}

interface AmazonPurchase {
  id: number;
  asin: string;
  amazon_status: string;
  total_cost: number | null;
  tracking_number: string | null;
  amazon_order_id: string | null;
  link_source: string | null;
  /** Cuenta de Amazon de otra empresa con la que se compró (rotación por límite). */
  purchase_account_seller_id?: number | null;
  purchase_account_name?: string | null;
}

interface Claim {
  ml_claim_id: number;
  status: string;
  type: string;
  stage: string;
  reason_id: string;
}

interface InternalPurchase {
  asin: string;
  internal_sku: string | null;
  quantity: number;
  unit_cost: number;
  total_cost: number;
  status: string;
}

interface EnrichedOrder {
  ml_order_id: number;
  seller_id: number;
  pack_id: number | null;
  status: string;
  paid_amount: number;
  currency_id: string;
  context_site: string;
  buyer_nickname: string;
  buyer_id: number;
  ml_shipment_id: number | null;
  date_created: string;
  date_closed: string | null;
  last_updated: string;
  has_claim: boolean;
  fulfilled: boolean;
  items: OrderItem[];
  shipment: Shipment | null;
  amazon_purchase: AmazonPurchase | null;
  internal_purchase: InternalPurchase | null;
  claim: Claim | null;
  profit: number | null;
  margin_pct: number | null;
}

interface OrderStats {
  total: number;
  paid: number;
  cancelled: number;
  total_revenue: number;
  last_24h: number;
  last_7d: number;
  last_30d: number;
  with_claims: number;
}

interface MessageItem {
  id: string;
  from: { user_id: number };
  to: { user_id: number };
  text: string;
  text_translated: string | null;
  message_date: { created: string; read: string | null };
  message_moderation: { status: string };
}

interface OrderDetail {
  order: EnrichedOrder;
  ml_order: unknown;
  ml_shipment: unknown;
  shipment_history: { status: string; substatus: string; date: string }[] | null;
  shipment_carrier: { name: string; url: string } | null;
  messages: { conversation_status: { status: string; substatus: string | null }; messages: MessageItem[] } | null;
}

const SITE_FLAGS: Record<string, string> = { MLA: '🇦🇷', MLM: '🇲🇽', MLC: '🇨🇱', MCO: '🇨🇴', MLU: '🇺🇾', MLB: '🇧🇷' };
const fmt = (n: number) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n);
const fmtN = (n: number) => new Intl.NumberFormat('en-US').format(n);

const SHIP_STATUS_MAP: Record<string, 'ok' | 'warning' | 'error' | 'info'> = {
  delivered: 'ok', shipped: 'info', ready_to_ship: 'warning', handling: 'warning',
  pending: 'unknown' as 'info', not_delivered: 'error', cancelled: 'error',
};

const ORDER_STATUS_MAP: Record<string, 'ok' | 'warning' | 'error' | 'info'> = {
  paid: 'ok', cancelled: 'error', payment_required: 'warning', payment_in_process: 'warning',
  partially_paid: 'warning', invalid: 'error',
};

const AMZ_STATUS_MAP: Record<string, 'ok' | 'warning' | 'error' | 'info'> = {
  ordered: 'ok', shipped: 'info', delivered: 'ok', completed: 'ok',
  processing: 'warning', pending_approval: 'warning', error: 'error', cancelled: 'error',
};

export default function OrdersTab({ sellerId }: { sellerId: number }) {
  const can = useCan();
  const canMoney = can('global.view_money');
  const canOperate = can('orders.operate');
  const { t } = useTranslation();
  const [orders, setOrders] = useState<EnrichedOrder[]>([]);
  const [stats, setStats] = useState<OrderStats | null>(null);
  const pag = usePagination('ml-orders');
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [siteFilter, setSiteFilter] = useState('');
  const [shipFilter, setShipFilter] = useState('');
  const [selectedOrder, setSelectedOrder] = useState<OrderDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [labelAction, setLabelAction] = useState<'idle' | 'generating' | 'merging'>('idle');
  const [labelResult, setLabelResult] = useState<string | null>(null);

  const toggleSelect = (orderId: number) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(orderId)) next.delete(orderId);
      else next.add(orderId);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selected.size === orders.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(orders.map(o => o.ml_order_id)));
    }
  };

  const handleGenerateLabels = async () => {
    if (selected.size === 0) return;
    setLabelAction('generating');
    setLabelResult(null);
    try {
      // Get shipment IDs for selected orders
      const shipmentIds = orders
        .filter(o => selected.has(o.ml_order_id) && o.ml_shipment_id)
        .map(o => o.ml_shipment_id);
      if (shipmentIds.length === 0) { setLabelResult('No shipments found for selected orders'); setLabelAction('idle'); return; }

      const res: any = await api.post('/labels/generate', {
        seller_id: sellerId,
        shipment_ids: shipmentIds,
        force: true,
      });
      if (res.success) {
        const ok = res.results?.filter((r: any) => r.success).length || 0;
        setLabelResult(`${ok}/${shipmentIds.length} labels generated`);
      } else {
        setLabelResult(res.error || 'Generation failed');
      }
    } catch (err: any) {
      setLabelResult(err.message || 'Error');
    }
    setLabelAction('idle');
  };

  const handleMergeSelected = async () => {
    if (selected.size === 0) return;
    setLabelAction('merging');
    setLabelResult(null);
    try {
      const shipmentIds = orders
        .filter(o => selected.has(o.ml_order_id) && o.ml_shipment_id)
        .map(o => o.ml_shipment_id);
      const res: any = await api.post('/labels/merge', {
        seller_id: sellerId,
        shipment_ids: shipmentIds,
        force: true,
      });
      if (res.success && res.data?.count > 0) {
        if (res.data.url) {
          window.open(res.data.url, '_blank');
        }
        const skippedMsg = res.data.skipped?.length ? ` | ⚠️ ${res.data.skipped.length} sin ML label` : '';
        setLabelResult(`${res.data.count} labels enviadas a impresora${skippedMsg}`);
      } else {
        setLabelResult(res.data?.message || res.error || 'No labels to merge');
      }
    } catch (err: any) {
      setLabelResult(err.message || 'Error');
    }
    setLabelAction('idle');
  };

  const handleMergeAllReadyToShip = async () => {
    setLabelAction('merging');
    setLabelResult(null);
    try {
      const res: any = await api.post('/labels/merge', { seller_id: sellerId });
      if (res.success && res.data?.count > 0) {
        if (res.data.url) {
          const link = document.createElement('a');
          link.href = res.data.url;
          link.download = `labels_all_${Date.now()}.pdf`;
          link.click();
        }
        const skippedMsg = res.data.skipped?.length ? ` | ⚠️ ${res.data.skipped.length} sin ML label` : '';
        setLabelResult(`${res.data.count} labels enviadas a impresora${res.data.generated ? ` (${res.data.generated} generadas)` : ''}${skippedMsg}`);
      } else {
        setLabelResult(res.data?.message || 'No pending labels');
      }
    } catch (err: any) {
      setLabelResult(err.message || 'Error');
    }
    setLabelAction('idle');
  };

  const loadOrders = useCallback(async () => {
    setLoading(true);
    const offset = (pag.page - 1) * pag.perPage;
    const params = new URLSearchParams({ seller_id: String(sellerId), limit: String(pag.perPage), offset: String(offset) });
    if (statusFilter) params.set('status', statusFilter);
    if (siteFilter) params.set('site', siteFilter);
    if (shipFilter) params.set('ship_filter', shipFilter);
    if (search) params.set('search', search);

    const res = await api.get<EnrichedOrder[]>(`/mercadolibre/orders?${params}`);
    if (res.success && res.data) {
      setOrders(res.data);
      pag.setTotal((res as any).meta?.total || 0);
      if ((res as any).stats) setStats((res as any).stats);
    }
    setLoading(false);
  }, [sellerId, pag.page, pag.perPage, statusFilter, siteFilter, shipFilter, search]);

  useEffect(() => { loadOrders(); }, [loadOrders]);

  // SSE real-time updates
  const { subscribe } = useSSEStore();
  const loadRef = useRef(loadOrders);
  loadRef.current = loadOrders;
  useEffect(() => {
    const refresh = () => loadRef.current();
    const unsubs = [
      subscribe('warehouse:item_closed', refresh),
      subscribe('claim:message', refresh),
      subscribe('claim:refunded', refresh),
      subscribe('claim:dispute', refresh),
      subscribe('question:answered', refresh),
    ];
    return () => unsubs.forEach(u => u());
  }, [subscribe]);

  const openDetail = async (orderId: number) => {
    setDetailLoading(true);
    const res = await api.get<OrderDetail>(`/mercadolibre/orders/${orderId}?seller_id=${sellerId}`);
    if (res.success && res.data) setSelectedOrder(res.data);
    setDetailLoading(false);
  };

  return (
    <div className="space-y-4">
      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-2">
          <MiniStat label="24h" value={stats.last_24h} />
          <MiniStat label="7d" value={stats.last_7d} />
          <MiniStat label="30d" value={stats.last_30d} />
          <MiniStat label={t('common.total')} value={stats.total} />
          <MiniStat label="Paid" value={stats.paid} color="text-green-400" />
          <MiniStat label="Cancelled" value={stats.cancelled} color="text-red-400" />
          <MiniStat label={t('ml.c.claims')} value={stats.with_claims} color="text-yellow-400" />
          {canMoney && <MiniStat label="Revenue" value={fmt(stats.total_revenue)} color="text-emerald-400" />}
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-0 sm:min-w-[200px] max-w-[300px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--color-text-muted)]" />
          <input
            value={search}
            onChange={(e) => { setSearch(e.target.value); pag.resetPage(); }}
            placeholder={t('ml.o.searchOrders')}
            className="w-full pl-9 pr-3 py-2 bg-[var(--color-bg-secondary)] border border-[var(--color-border)] rounded-lg text-sm text-[var(--color-text)]"
          />
        </div>

        <select
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value); pag.resetPage(); }}
          className="px-3 py-2 bg-[var(--color-bg-secondary)] border border-[var(--color-border)] rounded-lg text-sm text-[var(--color-text)]"
        >
          <option value="">All Status</option>
          <option value="paid">Paid</option>
          <option value="cancelled">Cancelled</option>
        </select>

        <select
          value={siteFilter}
          onChange={(e) => { setSiteFilter(e.target.value); pag.resetPage(); }}
          className="px-3 py-2 bg-[var(--color-bg-secondary)] border border-[var(--color-border)] rounded-lg text-sm text-[var(--color-text)]"
        >
          <option value="">All Sites</option>
          {['MLA', 'MLM', 'MLC', 'MCO', 'MLB'].map(s => (
            <option key={s} value={s}>{SITE_FLAGS[s]} {s}</option>
          ))}
        </select>

        <select
          value={shipFilter}
          onChange={(e) => { setShipFilter(e.target.value); pag.resetPage(); }}
          className="px-3 py-2 bg-[var(--color-bg-secondary)] border border-[var(--color-border)] rounded-lg text-sm text-[var(--color-text)]"
        >
          <option value="">{t('ml.o.allShipments')}</option>
          <option value="ready_to_ship">{t('ml.o.readyToShip')}</option>
          <option value="delivered">{t('ml.o.delivered')}</option>
          <option value="not_delivered">{t('ml.o.notDelivered')}</option>
        </select>

        {canOperate && <button
          onClick={handleMergeAllReadyToShip}
          disabled={labelAction !== 'idle'}
          className="flex items-center gap-1.5 px-3 py-2 bg-[var(--color-primary)] text-white text-sm rounded-lg hover:opacity-90 disabled:opacity-50"
        >
          {labelAction === 'merging' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Printer className="w-4 h-4" />}
          Merge & Print All
        </button>}

        <button
          onClick={loadOrders}
          className="p-2 text-[var(--color-text-muted)] hover:text-[var(--color-primary)]"
        >
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>

      {/* Label action result toast */}
      {labelResult && (
        <div className="flex items-center justify-between px-4 py-2 bg-[var(--color-bg-secondary)] border border-[var(--color-border)] rounded-lg text-sm">
          <span className="text-[var(--color-text)]">{labelResult}</span>
          <button onClick={() => setLabelResult(null)} className="text-[var(--color-text-muted)] hover:text-[var(--color-text)]"><X className="w-4 h-4" /></button>
        </div>
      )}

      {/* Floating action bar when items selected */}
      {selected.size > 0 && canOperate && (
        <div className="flex items-center justify-between px-4 py-3 bg-[var(--color-primary-light)] border border-[var(--color-primary)] rounded-lg">
          <span className="text-sm font-medium text-[var(--color-primary)]">
            {selected.size} {selected.size === 1 ? 'orden seleccionada' : 'ordenes seleccionadas'}
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={handleGenerateLabels}
              disabled={labelAction !== 'idle'}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {labelAction === 'generating' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileText className="w-3.5 h-3.5" />}
              Generar Labels
            </button>
            <button
              onClick={handleMergeSelected}
              disabled={labelAction !== 'idle'}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 disabled:opacity-50"
            >
              {labelAction === 'merging' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Printer className="w-3.5 h-3.5" />}
              Merge & Print
            </button>
            <button
              onClick={() => setSelected(new Set())}
              className="p-1.5 text-[var(--color-text-muted)] hover:text-[var(--color-text)]"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Orders table */}
      <div className="bg-[var(--color-bg-secondary)] rounded-lg border border-[var(--color-border)] overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[var(--color-border)]">
              <th className="p-3 w-8">
                {canOperate && <button onClick={toggleSelectAll} className="text-[var(--color-text-muted)] hover:text-[var(--color-primary)]">
                  {selected.size === orders.length && orders.length > 0
                    ? <CheckSquare className="w-4 h-4 text-[var(--color-primary)]" />
                    : <Square className="w-4 h-4" />}
                </button>}
              </th>
              <th className="text-left p-3 text-[var(--color-text-muted)] font-medium">Order</th>
              <th className="text-left p-3 text-[var(--color-text-muted)] font-medium">{t('ml.o.product')}</th>
              <th className="text-left p-3 text-[var(--color-text-muted)] font-medium">{t('ml.c.buyer')}</th>
              <th className="text-center p-3 text-[var(--color-text-muted)] font-medium">{t('ml.o.shipment')}</th>
              <th className="text-center p-3 text-[var(--color-text-muted)] font-medium">Amazon</th>
              <th className="text-right p-3 text-[var(--color-text-muted)] font-medium">{t('common.total')}</th>
              <th className="text-right p-3 text-[var(--color-text-muted)] font-medium">{t('ml.c.profit')}</th>
              <th className="text-left p-3 text-[var(--color-text-muted)] font-medium">{t('common.date')}</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={9} className="p-8 text-center text-[var(--color-text-muted)]">{t('common.loading')}</td></tr>
            ) : orders.length === 0 ? (
              <tr><td colSpan={9} className="p-8 text-center text-[var(--color-text-muted)]">{t('ml.o.noOrders')}</td></tr>
            ) : orders.map((o) => (
              <tr
                key={o.ml_order_id}
                onClick={() => openDetail(o.ml_order_id)}
                className={`border-b border-[var(--color-border)] hover:bg-[var(--color-bg)] cursor-pointer transition-colors ${
                  selected.has(o.ml_order_id) ? 'bg-[var(--color-primary-light)]/20' : ''
                }`}
              >
                {/* Checkbox */}
                <td className="p-3" onClick={e => e.stopPropagation()}>
                  {canOperate && <button onClick={() => toggleSelect(o.ml_order_id)} className="text-[var(--color-text-muted)] hover:text-[var(--color-primary)]">
                    {selected.has(o.ml_order_id)
                      ? <CheckSquare className="w-4 h-4 text-[var(--color-primary)]" />
                      : <Square className="w-4 h-4" />}
                  </button>}
                </td>
                {/* Order ID + site + status */}
                <td className="p-3">
                  <div className="flex items-center gap-2">
                    <span className="text-lg">{SITE_FLAGS[o.context_site] || '🌐'}</span>
                    <div>
                      <div className="text-xs"><EntityLink type="ml_order" value={o.ml_order_id} sellerId={sellerId} /></div>
                      <StatusBadge status={ORDER_STATUS_MAP[o.status] || 'info'} label={o.status} size="sm" />
                    </div>
                    {o.has_claim && <AlertTriangle className="w-3.5 h-3.5 text-yellow-400" />}
                  </div>
                </td>

                {/* Product */}
                <td className="p-3">
                  {o.items.length > 0 ? (
                    <div className="flex items-center gap-2">
                      {o.items[0].thumbnail && (
                        <img src={secureUrl(o.items[0].thumbnail)} alt="" className="w-8 h-8 rounded object-cover" />
                      )}
                      <div className="min-w-0">
                        <div className="text-xs text-[var(--color-text)]">
                          {o.items[0].title || o.items[0].item_id}
                        </div>
                        <div className="text-xs text-[var(--color-text-muted)] font-mono">
                          {o.items[0].seller_sku}
                          {o.items[0].quantity > 1 && <span className="ml-1 text-[var(--color-primary)] font-bold">x{o.items[0].quantity}</span>}
                        </div>
                        {o.items.length > 1 && (
                          <span className="text-xs text-[var(--color-primary)]">+{o.items.length - 1} more</span>
                        )}
                      </div>
                    </div>
                  ) : <span className="text-xs text-[var(--color-text-muted)]">-</span>}
                </td>

                {/* Buyer */}
                <td className="p-3 text-xs text-[var(--color-text)]">{o.buyer_nickname}</td>

                {/* Shipment */}
                <td className="p-3 text-center">
                  {o.shipment ? (
                    <StatusBadge
                      status={SHIP_STATUS_MAP[o.shipment.status] || 'info'}
                      label={o.shipment.status.replace(/_/g, ' ')}
                      size="sm"
                    />
                  ) : <span className="text-xs text-[var(--color-text-muted)]">-</span>}
                </td>

                {/* Amazon / Internal Stock */}
                <td className="p-3 text-center">
                  {o.amazon_purchase ? (
                    <StatusBadge
                      status={AMZ_STATUS_MAP[o.amazon_purchase.amazon_status] || 'info'}
                      label={o.amazon_purchase.amazon_status}
                      size="sm"
                    />
                  ) : o.internal_purchase ? (
                    <StatusBadge
                      status="ok"
                      label={`Stock (${o.internal_purchase.status})`}
                      size="sm"
                    />
                  ) : <span className="text-xs text-[var(--color-text-muted)]">-</span>}
                </td>

                {/* Amount */}
                <td className="p-3 text-right text-[var(--color-text)] font-medium">{canMoney ? fmt(o.paid_amount) : '\u2014'}</td>

                {/* Profit */}
                <td className="p-3 text-right">
                  {canMoney && o.profit !== null ? (
                    <span className={o.profit >= 0 ? 'text-green-400' : 'text-red-400'}>
                      {fmt(o.profit)} <span className="text-xs">({o.margin_pct}%)</span>
                    </span>
                  ) : <span className="text-xs text-[var(--color-text-muted)]">-</span>}
                </td>

                {/* Date */}
                <td className="p-3 text-xs text-[var(--color-text-muted)]">
                  {new Date(o.date_created).toLocaleDateString()}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <Pagination
        page={pag.page}
        totalPages={pag.totalPages}
        total={pag.total}
        perPage={pag.perPage}
        onPageChange={pag.setPage}
        onPerPageChange={pag.setPerPage}
      />

      {/* Detail panel */}
      {(selectedOrder || detailLoading) && (
        <OrderDetailPanel
          detail={selectedOrder}
          loading={detailLoading}
          sellerId={sellerId}
          onClose={() => setSelectedOrder(null)}
          onRefresh={() => selectedOrder && openDetail(selectedOrder.order.ml_order_id)}
          t={t}
        />
      )}
    </div>
  );
}

// ── Mini stat ──
function MiniStat({ label, value, color }: { label: string; value: number | string; color?: string }) {
  return (
    <div className="bg-[var(--color-bg-secondary)] border border-[var(--color-border)] rounded-lg p-2 text-center">
      <div className={`text-lg font-bold ${color || 'text-[var(--color-text)]'}`}>{value}</div>
      <div className="text-[10px] text-[var(--color-text-muted)]">{label}</div>
    </div>
  );
}

// ── Order detail panel ──
function OrderDetailPanel({
  detail, loading, sellerId, onClose, onRefresh, t
}: {
  detail: OrderDetail | null;
  loading: boolean;
  sellerId: number;
  onClose: () => void;
  onRefresh: () => void;
  t: (k: string) => string;
}) {
  const can = useCan();
  const canMoney = can('global.view_money');
  const [msgText, setMsgText] = useState('');
  const [sending, setSending] = useState(false);
  const [expandedSection, setExpandedSection] = useState<string | null>('shipment');

  const o = detail?.order;
  const toggleSection = (s: string) => setExpandedSection(expandedSection === s ? null : s);

  const sendMessage = async () => {
    if (!msgText.trim() || !o) return;
    setSending(true);
    const res = await api.post(`/mercadolibre/orders/${o.ml_order_id}/message?seller_id=${sellerId}`, {
      text: msgText,
      pack_id: o.pack_id,
    });
    if (res.success) {
      setMsgText('');
      onRefresh();
    }
    setSending(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-end">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative w-full max-w-2xl h-full bg-[var(--color-bg)] border-l border-[var(--color-border)] overflow-y-auto shadow-2xl">
        {loading || !detail || !o ? (
          <div className="p-8 text-center text-[var(--color-text-muted)]">{t('common.loading')}</div>
        ) : (
          <div className="p-4 space-y-4">
            {/* Header */}
            <div className="flex items-start justify-between">
              <div>
                <div className="flex items-center gap-2">
                  <span className="text-lg">{SITE_FLAGS[o.context_site] || '🌐'}</span>
                  <span className="text-lg font-bold text-[var(--color-text)]">
                    Order <EntityLink type="ml_order" value={o.ml_order_id} sellerId={sellerId} />
                  </span>
                  <StatusBadge status={ORDER_STATUS_MAP[o.status] || 'info'} label={o.status} size="sm" />
                </div>
                <div className="text-xs text-[var(--color-text-muted)] mt-1">
                  {new Date(o.date_created).toLocaleString()} &middot; {o.buyer_nickname}
                </div>
              </div>
              <button onClick={onClose} className="p-1 text-[var(--color-text-muted)] hover:text-[var(--color-text)]">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Items */}
            <div className="bg-[var(--color-bg-secondary)] rounded-lg border border-[var(--color-border)] p-3">
              <h4 className="text-sm font-medium text-[var(--color-text)] mb-2 flex items-center gap-2">
                <ShoppingCart className="w-4 h-4" /> {t('ml.o.items')}
              </h4>
              {o.items.map((item, i) => (
                <div key={i} className="flex items-center gap-3 py-2 border-t border-[var(--color-border)] first:border-0">
                  {item.thumbnail && <img src={secureUrl(item.thumbnail)} alt="" className="w-10 h-10 rounded object-cover" />}
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-[var(--color-text)]">{item.title || item.item_id}</div>
                    <div className="text-xs text-[var(--color-text-muted)]">
                      {item.seller_sku} &middot; x{item.quantity}
                    </div>
                  </div>
                  {canMoney && (
                  <div className="text-right">
                    <div className="text-sm text-[var(--color-text)]">{fmt(item.unit_price)}</div>
                    <div className="text-xs text-[var(--color-text-muted)]">Fee: {fmt(item.sale_fee)}</div>
                  </div>
                  )}
                </div>
              ))}
            </div>

            {/* Financial summary — bloque de puro dinero */}
            {canMoney && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              <div className="bg-[var(--color-bg-secondary)] rounded-lg border border-[var(--color-border)] p-3 text-center">
                <div className="text-xs text-[var(--color-text-muted)]">{t('ml.o.paidAmount')}</div>
                <div className="text-lg font-bold text-[var(--color-text)]">{fmt(o.paid_amount)}</div>
              </div>
              {o.amazon_purchase?.total_cost ? (
                <div className="bg-[var(--color-bg-secondary)] rounded-lg border border-[var(--color-border)] p-3 text-center">
                  <div className="text-xs text-[var(--color-text-muted)]">{t('ml.c.amazonCost')}</div>
                  <div className="text-lg font-bold text-orange-400">{fmt(o.amazon_purchase.total_cost)}</div>
                </div>
              ) : o.internal_purchase?.total_cost ? (
                <div className="bg-[var(--color-bg-secondary)] rounded-lg border border-[var(--color-border)] p-3 text-center">
                  <div className="text-xs text-[var(--color-text-muted)]">Stock Cost</div>
                  <div className="text-lg font-bold text-cyan-400">{fmt(o.internal_purchase.total_cost)}</div>
                </div>
              ) : null}
              {o.profit !== null && (
                <div className="bg-[var(--color-bg-secondary)] rounded-lg border border-[var(--color-border)] p-3 text-center">
                  <div className="text-xs text-[var(--color-text-muted)]">{t('ml.c.profit')}</div>
                  <div className={`text-lg font-bold ${o.profit >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                    {fmt(o.profit)}
                  </div>
                </div>
              )}
              {o.margin_pct !== null && (
                <div className="bg-[var(--color-bg-secondary)] rounded-lg border border-[var(--color-border)] p-3 text-center">
                  <div className="text-xs text-[var(--color-text-muted)]">Margin</div>
                  <div className={`text-lg font-bold ${o.margin_pct >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                    {o.margin_pct}%
                  </div>
                </div>
              )}
            </div>
            )}

            {/* Shipment section */}
            <CollapsibleSection
              title={`${t('ml.o.shipment')} ${o.shipment ? `— ${o.shipment.status}` : ''}`}
              icon={<Truck className="w-4 h-4" />}
              expanded={expandedSection === 'shipment'}
              onToggle={() => toggleSection('shipment')}
            >
              {o.shipment ? (
                <div className="space-y-2 text-xs">
                  <div className="flex flex-wrap gap-3">
                    <span><b>Status:</b> <StatusBadge status={SHIP_STATUS_MAP[o.shipment.status] || 'info'} label={o.shipment.status} size="sm" /></span>
                    {o.shipment.tracking_number && (
                      <span><b>Tracking:</b> <EntityLink type="tracking" value={o.shipment.tracking_number} sellerId={sellerId} /></span>
                    )}
                    {o.shipment.shipping_method_name && (
                      <span><b>Method:</b> {o.shipment.shipping_method_name}</span>
                    )}
                    {o.shipment.estimated_delivery_date && (
                      <span><b>ETA:</b> {new Date(o.shipment.estimated_delivery_date).toLocaleDateString()}</span>
                    )}
                  </div>

                  {/* Shipment history */}
                  {detail.shipment_history && detail.shipment_history.length > 0 && (
                    <div className="mt-2">
                      <div className="text-xs font-medium text-[var(--color-text)] mb-1">{t('ml.o.shipHistory')}</div>
                      {detail.shipment_history.map((h, i) => (
                        <div key={i} className="flex items-center gap-2 py-1">
                          <span className="text-[var(--color-text-muted)] min-w-[100px]">
                            {new Date(h.date).toLocaleString()}
                          </span>
                          <StatusBadge status={SHIP_STATUS_MAP[h.status] || 'info'} label={h.status} size="sm" />
                          {h.substatus && <span className="text-[var(--color-text-muted)]">({h.substatus})</span>}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Carrier */}
                  {detail.shipment_carrier && (
                    <div className="flex items-center gap-2 mt-2">
                      <span className="text-[var(--color-text-muted)]">Carrier:</span>
                      <span className="text-[var(--color-text)]">{detail.shipment_carrier.name}</span>
                      {detail.shipment_carrier.url && (
                        <a href={detail.shipment_carrier.url} target="_blank" rel="noopener" className="text-[var(--color-primary)]">
                          <ExternalLink className="w-3 h-3" />
                        </a>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-xs text-[var(--color-text-muted)]">{t('ml.o.noShipment')}</div>
              )}
            </CollapsibleSection>

            {/* Amazon purchase section */}
            {o.amazon_purchase && (
              <CollapsibleSection
                title={`Amazon — ${o.amazon_purchase.amazon_status}`}
                icon={<Package className="w-4 h-4" />}
                expanded={expandedSection === 'amazon'}
                onToggle={() => toggleSection('amazon')}
              >
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div><b>ASIN:</b> <EntityLink type="asin" value={o.amazon_purchase.asin} sellerId={sellerId} /></div>
                  <div><b>Status:</b> <StatusBadge status={AMZ_STATUS_MAP[o.amazon_purchase.amazon_status] || 'info'} label={o.amazon_purchase.amazon_status} size="sm" /></div>
                  {canMoney && o.amazon_purchase.total_cost && <div><b>Cost:</b> {fmt(o.amazon_purchase.total_cost)}</div>}
                  {o.amazon_purchase.purchase_account_name && (
                    <div className="col-span-2 text-amber-700 dark:text-amber-400">
                      <b>Comprada con la cuenta de Amazon de {o.amazon_purchase.purchase_account_name}</b> (la propia estaba en límite de cantidad)
                    </div>
                  )}
                  {o.amazon_purchase.amazon_order_id && (() => {
                    const ap = o.amazon_purchase!;
                    return <div className="flex items-center gap-2">
                      <span><b>AMZ Order:</b> <EntityLink type="amazon_order" value={ap.amazon_order_id!} sellerId={sellerId} /></span>
                      {can('orders.cancel_purchase') && !['cancelled', 'delivered', 'delivered_ml'].includes(ap.amazon_status) && (
                        <button
                          onClick={async (e) => {
                            e.stopPropagation();
                            if (!confirm(`Cancel Amazon order ${ap.amazon_order_id}?\n\nIMPORTANT: You must also cancel it on Amazon.com manually. This only updates our system.`)) return;
                            try {
                              await api.post(`/amazon/purchases/${ap.id}/cancel?seller_id=${sellerId}`, {
                                reason: 'Cancelled from order detail'
                              });
                              onRefresh();
                            } catch (err: any) {
                              alert(err.response?.data?.error || err.message);
                            }
                          }}
                          className="px-1.5 py-0.5 bg-red-600 hover:bg-red-700 text-white rounded text-[10px] font-medium transition-colors"
                          title="Cancel this Amazon purchase (must also cancel on Amazon.com)"
                        >
                          Cancel
                        </button>
                      )}
                    </div>;
                  })()}
                  {o.amazon_purchase.tracking_number && <div><b>Tracking:</b> <EntityLink type="tracking" value={o.amazon_purchase.tracking_number} sellerId={sellerId} /></div>}
                  {o.amazon_purchase.link_source && <div><b>Source:</b> {o.amazon_purchase.link_source}</div>}
                </div>
              </CollapsibleSection>
            )}

            {/* Internal stock purchase section */}
            {o.internal_purchase && !o.amazon_purchase && (
              <div className="bg-cyan-500/5 border border-cyan-500/20 rounded-lg p-3">
                <div className="flex items-center gap-2 text-sm">
                  <Package className="w-4 h-4 text-cyan-400" />
                  <span className="text-cyan-400 font-medium">Stock Interno</span>
                  <StatusBadge status="ok" label={o.internal_purchase.status} size="sm" />
                </div>
                <div className="grid grid-cols-2 gap-2 text-xs mt-2">
                  <div><b>SKU:</b> {o.internal_purchase.internal_sku || o.internal_purchase.asin}</div>
                  <div><b>Qty:</b> {o.internal_purchase.quantity}</div>
                  {canMoney && <div><b>Unit Cost:</b> {fmt(o.internal_purchase.unit_cost)}</div>}
                  {canMoney && <div><b>Total Cost:</b> {fmt(o.internal_purchase.total_cost)}</div>}
                </div>
              </div>
            )}

            {/* Claim section */}
            {o.claim && (
              <div className="bg-yellow-500/5 border border-yellow-500/20 rounded-lg p-3">
                <div className="flex items-center gap-2 text-sm">
                  <AlertTriangle className="w-4 h-4 text-yellow-400" />
                  <span className="text-yellow-400 font-medium">{t('ml.c.claimDetail')}</span>
                  <span className="text-xs text-[var(--color-text-muted)]">#{o.claim.ml_claim_id}</span>
                  <StatusBadge status={o.claim.status === 'opened' ? 'warning' : 'ok'} label={o.claim.status} size="sm" />
                  <span className="text-xs text-[var(--color-text-muted)]">{o.claim.stage} — {o.claim.reason_id}</span>
                </div>
              </div>
            )}

            {/* Messages section */}
            <CollapsibleSection
              title={`${t('ml.c.messages')} (${detail.messages?.messages?.length || 0})`}
              icon={<MessageCircle className="w-4 h-4" />}
              expanded={expandedSection === 'messages'}
              onToggle={() => toggleSection('messages')}
            >
              <div className="space-y-3">
                {detail.messages?.messages?.map((msg, i) => (
                  <div key={i} className={`p-3 rounded-lg text-sm ${
                    msg.from.user_id === o.buyer_id
                      ? 'bg-[var(--color-bg)] border border-[var(--color-border)] mr-4'
                      : 'bg-blue-500/10 border border-blue-500/20 ml-4'
                  }`}>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs font-medium text-[var(--color-text-muted)]">
                        {msg.from.user_id === o.buyer_id ? 'Buyer' : 'Seller'}
                      </span>
                      <span className="text-xs text-[var(--color-text-muted)]">
                        {new Date(msg.message_date.created).toLocaleString()}
                      </span>
                    </div>
                    <div className="text-[var(--color-text)]">{msg.text}</div>
                    {msg.text_translated && (
                      <div className="text-xs text-[var(--color-text-muted)] mt-1 italic">{msg.text_translated}</div>
                    )}
                  </div>
                ))}

                {/* Send message */}
                {detail.messages?.conversation_status?.status === 'active' && can('orders.messages') && (
                  <div className="space-y-2">
                    <textarea
                      value={msgText}
                      onChange={(e) => setMsgText(e.target.value)}
                      placeholder={t('ml.o.messagePlaceholder')}
                      className="w-full p-3 bg-[var(--color-bg)] border border-[var(--color-border)] rounded-lg text-sm text-[var(--color-text)] resize-none"
                      rows={3}
                    />
                    <button
                      onClick={sendMessage}
                      disabled={sending || !msgText.trim()}
                      className="flex items-center gap-1 px-3 py-1.5 bg-[var(--color-primary)] text-white rounded-lg text-xs hover:opacity-90 disabled:opacity-50"
                    >
                      <Send className="w-3 h-3" />
                      {sending ? '...' : t('ml.o.sendMessage')}
                    </button>
                  </div>
                )}

                {detail.messages?.conversation_status?.status === 'blocked' && (
                  <div className="text-xs text-[var(--color-text-muted)] p-2 bg-[var(--color-bg)] rounded">
                    {t('ml.o.conversationBlocked')}: {detail.messages.conversation_status.substatus || 'blocked'}
                  </div>
                )}
              </div>
            </CollapsibleSection>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Collapsible section ──
function CollapsibleSection({
  title, icon, expanded, onToggle, children
}: {
  title: string; icon: React.ReactNode; expanded: boolean; onToggle: () => void; children: React.ReactNode;
}) {
  return (
    <div className="bg-[var(--color-bg-secondary)] rounded-lg border border-[var(--color-border)] overflow-hidden">
      <button
        onClick={onToggle}
        className="w-full flex items-center gap-2 p-3 text-sm font-medium text-[var(--color-text)] hover:bg-[var(--color-bg)] transition-colors"
      >
        {icon}
        {title}
        <span className="ml-auto">
          {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </span>
      </button>
      {expanded && <div className="p-3 pt-0">{children}</div>}
    </div>
  );
}
